import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { AUTHOR_TITLE_OPTIONS, isAuthorTitleValue } from '../../utils/authorTitle';
import {
  BOOK_LANGUAGE_FORM_EN_ID,
  buildDisplayLanguages,
  useBookLanguagesForForm,
} from '../Settings/lists/bookLanguageFormSupport';
import { bookLanguageFlagDisplaySrc } from '../Settings/lists/singleTableListSupport';

/** Aligns with `AuthorModel.name_map`: keys are book language ids (e.g. mn, en). */
export type AuthorFormDraft = {
  mode: 'create' | 'edit';
  id: string;
  /** Title code; empty string = none. */
  title: string;
  name_map: Record<string, string>;
};

type AuthorFormProps = {
  draft: AuthorFormDraft;
  onChange: (next: AuthorFormDraft) => void;
};

export default function AuthorForm({ draft, onChange }: AuthorFormProps) {
  const { rows: fetched, loading, error } = useBookLanguagesForForm(true);
  const languages = useMemo(() => buildDisplayLanguages(fetched), [fetched]);

  const setNameForLang = (langId: string, value: string) => {
    onChange({ ...draft, name_map: { ...draft.name_map, [langId]: value } });
  };

  const setTitle = (value: string) => {
    onChange({ ...draft, title: isAuthorTitleValue(value) ? value : '' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} aria-label="Ачааллаж байна" />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2}>
      {draft.mode === 'edit' && (
        <TextField label="ID" name="id" value={draft.id} disabled autoComplete="off" fullWidth margin="none" />
      )}
      <FormControl fullWidth margin="dense">
        <InputLabel id="author-form-title-label" shrink>
          Цол зэрэг (заавал биш)
        </InputLabel>
        <Select<string>
          labelId="author-form-title-label"
          id="author-form-title"
          label="Цол зэрэг (заавал биш)"
          value={draft.title}
          onChange={(e) => setTitle(e.target.value)}
          displayEmpty
          renderValue={(v) => AUTHOR_TITLE_OPTIONS.find((o) => o.value === v)?.label ?? 'Байхгүй'}
        >
          {AUTHOR_TITLE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value === '' ? '__none' : opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">
        Зохиолчийн нэрийг хэл бүр дээр оруулна. Англи нэр заавал. Орчуулж болохгүй бол англиар оруулна уу.
      </Typography>
      {languages.map((lang) => {
        const isEn = lang.id === BOOK_LANGUAGE_FORM_EN_ID;
        const val = draft.name_map[lang.id] ?? '';
        const flagSrc = bookLanguageFlagDisplaySrc(lang);
        return (
          <TextField
            key={lang.id}
            label={lang.name}
            value={val}
            onChange={(e) => setNameForLang(lang.id, e.target.value)}
            required={isEn}
            fullWidth
            autoComplete="off"
            margin="dense"
            inputProps={{ 'aria-required': isEn }}
            InputProps={{
              startAdornment: flagSrc ? (
                <InputAdornment position="start" sx={{ mr: 0 }}>
                  <Box
                    component="img"
                    src={flagSrc}
                    alt=""
                    sx={{
                      width: 28,
                      height: 18,
                      objectFit: 'cover',
                      borderRadius: 0.5,
                      border: 1,
                      borderColor: 'divider',
                      display: 'block',
                    }}
                  />
                </InputAdornment>
              ) : undefined,
            }}
          />
        );
      })}
    </Stack>
  );
}

export function validateAuthorFormDraft(draft: AuthorFormDraft): string | null {
  if (draft.mode === 'edit' && !draft.id.trim()) return 'ID алга.';
  const en = (draft.name_map[BOOK_LANGUAGE_FORM_EN_ID] ?? '').trim();
  if (!en) return 'Англи нэр заавал бөглөнө.';
  if (!isAuthorTitleValue(draft.title)) return 'Цол зэргийн утга буруу байна.';
  return null;
}

/** Trims entries; caller must run `validateAuthorFormDraft` first so `en` is present. */
export function authorDraftToPayloadNameMap(draft: AuthorFormDraft): Record<string, string> {
  const name_map: Record<string, string> = {};
  for (const [code, raw] of Object.entries(draft.name_map)) {
    const t = raw.trim();
    if (t) name_map[code] = t;
  }
  return name_map;
}

/** Request body for `POST /authors` and `PUT /authors`. */
export function authorDraftToApiBody(draft: AuthorFormDraft): {
  name_map: Record<string, string>;
  title: string;
} {
  return {
    name_map: authorDraftToPayloadNameMap(draft),
    title: draft.title.trim() === '' ? '' : draft.title,
  };
}
